import { useEffect, useRef, useState } from 'react';
import { Button } from '@mieweb/ui';
import * as api from '../lib/api';
import { isDesktop, openInSystemBrowser } from '../lib/desktop';

interface SocialLoginButtonsProps {
  /** Path to return to after login (forwarded to the OAuth start endpoint). */
  redirect?: string | null;
  /** 'signup' when the user is creating an account. */
  mode?: string | null;
  /** Notifies the parent whether any provider is available (to render dividers). */
  onAvailabilityChange?: (available: boolean) => void;
}

// Apple and Google are the priority; LinkedIn is last (nice-to-have).
const PROVIDER_ORDER: api.OAuthProvider[] = ['google', 'apple', 'bluehive', 'linkedin'];

const PROVIDER_LABELS: Record<api.OAuthProvider, string> = {
  google: 'Continue with Google',
  apple: 'Continue with Apple',
  bluehive: 'Continue with BlueHive',
  linkedin: 'Continue with LinkedIn',
};

function ProviderIcon({ provider }: { provider: api.OAuthProvider }) {
  switch (provider) {
    case 'google':
      return (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
          <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
          <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
          <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
      );
    case 'apple':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M16.36 12.78c.02 2.6 2.28 3.46 2.3 3.47-.02.06-.36 1.24-1.19 2.46-.72 1.05-1.46 2.1-2.63 2.12-1.15.02-1.52-.68-2.83-.68-1.32 0-1.72.66-2.81.7-1.13.04-1.99-1.14-2.72-2.19-1.48-2.14-2.6-6.04-1.09-8.68a4.23 4.23 0 0 1 3.58-2.18c1.11-.02 2.16.75 2.83.75.68 0 1.95-.92 3.29-.79.56.03 2.13.23 3.14 1.7-.08.05-1.87 1.1-1.85 3.27M14.2 4.04c.6-.73 1-1.74.89-2.75-.86.03-1.9.57-2.52 1.3-.55.64-1.04 1.67-.91 2.66.96.07 1.94-.49 2.54-1.21" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
          <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm1.78 13.02H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
        </svg>
      );
    case 'bluehive':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#2563eb" aria-hidden="true">
          <path d="M12 2l8.66 5v10L12 22l-8.66-5V7L12 2zm0 2.31L5.34 8.15v7.7L12 19.69l6.66-3.84v-7.7L12 4.31z" />
          <path d="M12 7.5l4.33 2.5v5L12 17.5 7.67 15v-5L12 7.5z" />
        </svg>
      );
  }
}

export function SocialLoginButtons({
  redirect,
  mode,
  onAvailabilityChange,
}: SocialLoginButtonsProps) {
  const [providers, setProviders] = useState<api.OAuthProviderAvailability | null>(null);

  // Keep the latest callback without re-triggering the fetch effect.
  const availabilityCbRef = useRef(onAvailabilityChange);
  availabilityCbRef.current = onAvailabilityChange;

  useEffect(() => {
    let cancelled = false;
    api
      .getOAuthProviders()
      .then((p) => {
        if (cancelled) return;
        setProviders(p);
        availabilityCbRef.current?.(p.google || p.apple || p.linkedin || p.bluehive);
      })
      .catch(() => {
        if (cancelled) return;
        availabilityCbRef.current?.(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!providers) return null;

  const enabled = PROVIDER_ORDER.filter((p) => providers[p]);
  if (enabled.length === 0) return null;

  const start = (provider: api.OAuthProvider) => {
    if (isDesktop) {
      // Providers (notably Google) block sign-in inside embedded webviews —
      // run the flow in the real browser; the worker bounces the one-time
      // code back through the `pollenate://auth/callback` deep link.
      void openInSystemBrowser(
        api.getOAuthStartUrl(provider, { redirect, mode, client: 'desktop' }),
      );
      return;
    }
    window.location.href = api.getOAuthStartUrl(provider, { redirect, mode });
  };

  return (
    <div className="space-y-3">
      {enabled.map((provider) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          fullWidth
          size="lg"
          onClick={() => start(provider)}
        >
          <span className="flex items-center justify-center gap-3">
            <ProviderIcon provider={provider} />
            {PROVIDER_LABELS[provider]}
          </span>
        </Button>
      ))}
    </div>
  );
}
